import { map, share, flatMap } from 'rxjs/operators';
import { Injectable, OnInit } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { Inspection } from '../models/index';
import * as _ from 'lodash';
import { ApiService } from './api.service';
import { DataService } from './data.service';
import { BaseInspectionService } from './baseInspection.service';
import { AuthorizationService } from './authorization.service';
import { OfflineApiService } from './offlineApi.service';
import { AppConstants } from '../../utils/appConstants.class';
import { LogService } from './log.service';

@Injectable({
  providedIn: 'root'
})
export class InspectionModelService extends BaseInspectionService {
  constructor(
    protected apiService: ApiService,
    protected dataService: DataService,
    private readonly authorize: AuthorizationService,
    private logService: LogService,
    private offlineApi: OfflineApiService
  ) {
    super(apiService, dataService);
    this.loadInitialData();
  }

  public getModels(): Observable<Inspection[]> {
    if (this.loader) {
      return this.loader;
    }
    return this.dataService.getModels();
  }

  public getPublishedModels(): Observable<Inspection[]> {
    if (this.loader) {
      return this.loader.pipe(
        flatMap((data) => {
          return this.dataService.getPublishedModels();
        })
      );
    }
    return this.dataService.getPublishedModels();
  }

  loadInitialData() {
    this.loader = this.loadAllModels().pipe(
      share(),
      map((data: Inspection[]) => {
        const models = data.filter((m) => m.inspectionType === 200);
        this.dataService.processInspections(models);
        this.loader = null;
        return models;
      })
    );

    this.loader.subscribe(
      (data: any) => {},
      (err: any) => this.logService.logError(err, 'Load all models error')
    );
  }

  private loadAllModels() {
    if (this.isManager && this.authorize.isAuthorize('manage.template')) {
      const search = { page: 0, pageSize: 100000, filters: [{ type: '1300', value: '200' }] };
      const url = '/models/list';
      return this.apiService.post(url, search).pipe(map((data) => (data.Items ? data.Items : data)));
    }
    return this.loadAllModelsIfNotTemplateManager();
  }

  private loadAllModelsIfNotTemplateManager() {
    // be careful here, api methods have not the same arguments
    const search = { page: 0, pageSize: 100000, filters: [] };
    const url = '/inspections/models/list';

    if (this.isManager) {
      return this.apiService.post(url, { search }).pipe(map((data) => (data.Items ? data.Items : data)));
    }

    const table = AppConstants.INDEXEDDB_TABLES.templates;
    const lastSyncDate = localStorage.getItem(
      `${table}${AppConstants.LAST_SYNC_DATE}_${this.applicationType}_${this.currentUserId}`
    );
    return this.offlineApi.postSyncList(url, { search, lastSyncDate }, table);
  }

  public getFullModels(): Observable<any> {
    const search = { page: 0, pageSize: 100000, filters: [] };
    const url = '/inspections/models/list/full';
    const table = AppConstants.INDEXEDDB_TABLES.templates;
    const dateKey = `${table}_full${AppConstants.LAST_SYNC_DATE}_${this.applicationType}_${this.currentUserId}`;
    const lastSyncDate = localStorage.getItem(dateKey);
    return this.offlineApi.postSyncList(url, { search, lastSyncDate }, table, dateKey);
  }

  public getModel(id: string): Observable<Inspection> {
    const inspection = this.dataService.getInspection(id);

    if (inspection && inspection.isLoaded) {
      return new BehaviorSubject(inspection).asObservable();
    }

    return this._getModel(id).pipe(
      map((model: any) => {
        model.isLoaded = true;
        this.dataService.processInspection(model);
        this.dataService.processSectionItems(model, model.sectionsItems);
        return this.dataService.getInspection(id);
      })
    );
  }

  private _getModel(id: string): Observable<Inspection> {
    const url = `/models/${id}`;
    if (this.isManager) {
      return this.apiService.get(url);
    }
    return this.offlineApi.getSyncUpdates(url, 'templates', id);
  }

  public newModel(params: any): Observable<Inspection> {
    const url = '/models';

    return this.apiService.post(url, params).pipe(
      map((inspection: Inspection) => {
        this.dataService.processInspection(inspection);
        return inspection;
      })
    );
  }

  public updateStatus(id: string, status: number): any {
    const url = '/models/' + id + '/status/' + status;
    return this.apiService.post(url).pipe(
      map((success: boolean) => {
        if (success) {
          this.dataService.updateStatus(id, status);
        }
      })
    );
  }

  public updateInspectionProperties(id: string, params: any): Observable<Inspection> {
    const url = '/models/' + id;
    return this.apiService.patch(url, params).pipe(
      map((inspection: Inspection) => {
        inspection.isLoaded = true;
        this.dataService.processInspection(inspection);
        return inspection;
      })
    );
  }

  public addSection(id: string, sourceInspectionId: string, sourceSectionId: string[]): Observable<Inspection> {
    const url = `/models/${id}/sections`;
    return this.apiService
      .post(url, { id: id, sourceInspectionId: sourceInspectionId, SourceSectionsId: sourceSectionId })
      .pipe(
        map((inspection: Inspection) => {
          inspection.isLoaded = true;
          this.dataService.processInspection(inspection);
          this.dataService.updateSectionsOrder(inspection);
          return inspection;
        })
      );
  }

  public addPoints(
    id: string,
    targetSectionId: string,
    sourceInspectionId: string,
    sourcePointsId: string[],
    targetFolderId: string
  ): Observable<Inspection> {
    const url = '/models/' + id + '/points';
    return this.apiService
      .post(url, {
        id: id,
        sourceInspectionId: sourceInspectionId,
        TargetSectionId: targetSectionId,
        SourcePointsId: sourcePointsId,
        TargetFolderId: targetFolderId
      })
      .pipe(
        map((inspection: Inspection) => {
          this.dataService.processInspection(inspection);
          return this.dataService.getInspection(id);
        })
      );
  }

  attachTemplates(id: string, templates: string[]) {
    return this.apiService.patch(`/models/${id}/attachTemplates`, templates);
  }

  public getStatusList(): number[] {
    return [0, 100, -200];
  }
}
